
// Deployment trigger: 2026-04-24
import React, { useState } from 'react';
import { Copy, Check, Globe, Code, QrCode } from 'lucide-react';

interface EmbedCodeProps {
  campaignId?: string;
  projectId?: string;
  domain?: string;
}

const EmbedCode: React.FC<EmbedCodeProps> = ({ campaignId, projectId, domain }) => {
  const [copiedType, setCopiedType] = useState<'campaign' | 'project' | 'url' | null>(null);
  
  // Always use myeplay.com for embed codes as requested by the user
  const baseUrl = 'https://myeplay.com';

  const campaignScript = campaignId ? `<script src="${baseUrl}/widget/v1.js" data-id="${campaignId}" async></script>` : '';
  const projectScript = projectId ? `<script src="${baseUrl}/p/${projectId}.js" async></script>` : '';
  
  const campaignUrl = campaignId ? `${baseUrl}/?campaign=${campaignId}` : '';
  const projectUrl = baseUrl;
  
  const displayUrl = campaignId ? campaignUrl : projectUrl;
  const qrUrl = displayUrl ? `https://api.qrserver.com/v1/create-qr-code/?size=250x250&data=${encodeURIComponent(displayUrl)}` : '';

  const handleCopy = (text: string, type: 'campaign' | 'project' | 'url') => {
    navigator.clipboard.writeText(text);
    setCopiedType(type);
    setTimeout(() => setCopiedType(null), 2000);
  };

  return (
    <div className="max-w-3xl mx-auto py-10 space-y-12">
      <div className="text-center">
        <h3 className="text-2xl font-bold text-slate-800 mb-2">Deploy Your Growth Engine</h3>
        <p className="text-slate-500">
          {campaignId 
            ? "Choose between a single game widget or the project-wide master script." 
            : "Use the project-wide master script or QR code for your business."}
        </p>
      </div>

      <div className="grid grid-cols-1 gap-8">
        {/* Project Master Script */}
        {projectId && (
          <div className="bg-white border-2 border-indigo-50 rounded-3xl overflow-hidden shadow-xl shadow-indigo-50/50">
            <div className="px-6 py-4 bg-indigo-600 text-white flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Globe className="w-5 h-5" />
                <span className="font-bold text-sm">Master Project Script (Recommended)</span>
              </div>
              <span className="text-[10px] bg-white/20 px-2 py-0.5 rounded uppercase font-black">All Games</span>
            </div>
            <div className="p-6">
              <p className="text-xs text-slate-500 mb-4 leading-relaxed">
                Paste this once in your footer. It will load <strong>any active games</strong> you create for this project automatically.
              </p>
              <div className="bg-slate-900 rounded-xl p-4 relative group">
                <code className="text-indigo-300 text-xs block break-all font-mono leading-relaxed pr-10">
                  {projectScript}
                </code>
                <button 
                  onClick={() => handleCopy(projectScript, 'project')}
                  className="absolute right-3 top-3 p-2 bg-slate-800 text-indigo-400 hover:text-white rounded-lg transition-all"
                >
                  {copiedType === 'project' ? <Check className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4" />}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Single Campaign Script */}
        {campaignId && (
          <div className="bg-white border-2 border-slate-100 rounded-3xl overflow-hidden">
            <div className="px-6 py-4 bg-slate-900 text-white flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Code className="w-5 h-5 text-indigo-400" />
                <span className="font-bold text-sm">Individual Game Widget</span>
              </div>
            </div>
            <div className="p-6">
              <p className="text-xs text-slate-500 mb-4 leading-relaxed">
                Use this if you want to load <strong>only this specific game</strong> on a particular landing page.
              </p>
              <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 relative group">
                <code className="text-slate-600 text-xs block break-all font-mono leading-relaxed pr-10">
                  {campaignScript}
                </code>
                <button 
                  onClick={() => handleCopy(campaignScript, 'campaign')}
                  className="absolute right-3 top-3 p-2 bg-white border border-slate-200 text-slate-400 hover:text-indigo-600 rounded-lg shadow-sm transition-all"
                >
                  {copiedType === 'campaign' ? <Check className="w-4 h-4 text-emerald-500" /> : <Copy className="w-4 h-4" />}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* QR Code & Direct Link */}
        {displayUrl && (
          <div className="bg-white border-2 border-slate-100 rounded-3xl overflow-hidden">
            <div className="px-6 py-4 bg-slate-100 text-slate-800 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <QrCode className="w-5 h-5 text-slate-600" />
                <span className="font-bold text-sm">In-Store QR Code & Direct Link</span>
              </div>
            </div>
            <div className="p-6 flex flex-col md:flex-row items-center gap-8">
              <div className="shrink-0 bg-white p-4 rounded-2xl border-2 border-slate-100 shadow-sm">
                <img src={qrUrl} alt="QR Code" className="w-32 h-32" />
              </div>
              <div className="flex-1 w-full">
                <p className="text-xs text-slate-500 mb-4 leading-relaxed">
                  Print this QR code for your physical store, or use the direct link below for social media bios (Instagram, TikTok) and email newsletters.
                </p>
                <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 relative group">
                  <code className="text-slate-600 text-xs block break-all font-mono leading-relaxed pr-10">
                    {displayUrl}
                  </code>
                  <button 
                    onClick={() => handleCopy(displayUrl, 'url')}
                    className="absolute right-3 top-3 p-2 bg-white border border-slate-200 text-slate-400 hover:text-indigo-600 rounded-lg shadow-sm transition-all"
                  >
                    {copiedType === 'url' ? <Check className="w-4 h-4 text-emerald-500" /> : <Copy className="w-4 h-4" />}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="pt-6 border-t border-slate-100">
         <div className="flex flex-col sm:flex-row items-center justify-center gap-8 text-center sm:text-left">
            <div className="flex items-center gap-3">
               <div className="w-10 h-10 rounded-full bg-emerald-50 flex items-center justify-center text-emerald-600 font-bold italic">1</div>
               <p className="text-xs font-bold text-slate-700">Copy Code</p>
            </div>
            <div className="flex items-center gap-3">
               <div className="w-10 h-10 rounded-full bg-indigo-50 flex items-center justify-center text-indigo-600 font-bold italic">2</div>
               <p className="text-xs font-bold text-slate-700">Paste in &lt;body&gt;</p>
            </div>
            <div className="flex items-center gap-3">
               <div className="w-10 h-10 rounded-full bg-amber-50 flex items-center justify-center text-amber-600 font-bold italic">3</div>
               <p className="text-xs font-bold text-slate-700">Publish & Prosper</p>
            </div>
         </div>
      </div>
    </div>
  );
};

export default EmbedCode;
